import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getCacheKeyForPlanning } from './usePlanning';
import { getCacheKeyForHousePrices } from './useHousePrices';

const initialSearchState = {
  validatedAddress: null,
  selectedAddress: null,
  savedSearchId: null,
  lastSalePrice: 0,
  postcode: null,
  isLoadingSearchAddress: false,
};

export const useSearchState = () => {
  const queryClient = useQueryClient();

  const { data: searchState } = useQuery('searchState', () => initialSearchState, {
    initialData: initialSearchState,
    refetchOnWindowFocus: false,
    retryOnMount: false,
    refetchOnMount: false,
    staleTime: Infinity,
  });

  const mutation = useMutation((newState) => newState, {
    onMutate: (newState) => {
      queryClient.setQueryData('searchState', (oldState) => ({
        ...oldState,
        ...newState,
      }));
    },
  });

  const updateLoadingState = async (isLoading) => {
    await mutation.mutateAsync({
      isLoadingSearchAddress: isLoading,
    });
  };

  const updatePostCode = async (postcode) => {
    await mutation.mutateAsync({
      postcode,
    });
  };

  const selectPropertyFromHistory = async (validatedAddress, savedSearchId) => {
    const planningData = validatedAddress?.response_data?.planning;
    const housePricesData = validatedAddress?.response_data?.housePrices;
    const lastSalePriceData = validatedAddress?.response_data?.lastSalePrice;

    const planningCacheKey = [
      'planningData',
      getCacheKeyForPlanning(validatedAddress, 0.2),
      savedSearchId,
    ];
    const housePricesCacheKey = [
      'housePrices',
      getCacheKeyForHousePrices(validatedAddress?.postcode),
      savedSearchId,
    ];

    queryClient.setQueryData(planningCacheKey, planningData);
    queryClient.setQueryData(housePricesCacheKey, housePricesData);
    queryClient.setQueryData('searchState', (state) => ({
      ...state,
      lastSalePrice: lastSalePriceData,
    }));

    await mutation.mutateAsync({
      validatedAddress,
      savedSearchId,
    });
  };

  const selectPropertyFromSearch = async (selectedProperty) => {
    const validatedAddress = {
      address: `${selectedProperty.paon || ''} ${selectedProperty.street || ''}, ${
        selectedProperty.town || ''
      }`.trim(),
      postcode: selectedProperty.postcode,
      lat: selectedProperty.latitude,
      lng: selectedProperty.longitude,
      flat_number: selectedProperty.saon || '',
      street: selectedProperty.street,
      street_number: selectedProperty.paon,
      property_type: selectedProperty.propertyType || 'buy',
    };

    const selectedAddress = {
      address: validatedAddress.address,
      flat_number: validatedAddress.flat_number,
      postcode: validatedAddress.postcode,
      lat: validatedAddress.lat,
      lng: validatedAddress.lng,
      property_type: selectedProperty.propertyType || 'buy',
      search_type: 'free',
      search_timestamp: new Date().toISOString(),
    };

    await mutation.mutateAsync({
      validatedAddress,
      selectedAddress,
      savedSearchId: null,
      lastSalePrice: selectedProperty.price || initialSearchState.lastSalePrice || 0,
    });
  };

  return {
    searchState,
    selectPropertyFromSearch,
    updatePostCode,
    selectPropertyFromHistory,
    updateLoadingState,
  };
};

export default useSearchState;
